import React from 'react';
import {
  Box,
  VStack,
  Flex,
  Button,
  Icon,
  Heading,
  Avatar,
  Badge,
  Text,
  Divider,
  useColorModeValue,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaTasks,
  FaUser,
  FaSignOutAlt,
  FaChartBar,
  FaProjectDiagram,
  FaUsers,
  FaHome,
  FaUsersCog,
  FaCog,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import { canAccessTab } from '../../utils/permissions';
import { AuthenticatedUser } from '../../contexts/AuthenticatedUserInfoContext';
import { useAuth } from '../../contexts/AuthContext';
import  Logo  from '../../assets/logo.png';
import { APP_NAME } from '../../config/branding';

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onLogout: () => void;
  userInfo?: {
    firstName?: string;
    lastName?: string;
    role?: string;
  };
  authenticatedUser?: AuthenticatedUser | null;
  isMinimized: boolean;
  onToggleMinimize: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  onTabChange,
  onLogout,
  userInfo,
  authenticatedUser,
  isMinimized,
  onToggleMinimize,
}) => {
  const { organization } = useAuth();
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const allMenuItems = [
    { id: 'overview', label: 'Overview', icon: FaHome, colorScheme: 'blue', route: '/dashboard/overview' },
    { id: 'tasks', label: 'My Tasks', icon: FaTasks, colorScheme: 'blue', route: '/dashboard/tasks' },
    { id: 'projects', label: 'Projects', icon: FaProjectDiagram, colorScheme: 'green', route: '/dashboard/projects' },
    { id: 'clients', label: 'Clients', icon: FaUsers, colorScheme: 'purple', route: '/dashboard/clients' },
    { id: 'analytics', label: 'Analytics', icon: FaChartBar, colorScheme: 'orange', route: '/dashboard/analytics' },
    { id: 'users', label: 'Manage Users', icon: FaUsersCog, colorScheme: 'red', route: '/dashboard/users' },
    { id: 'settings', label: 'Settings', icon: FaCog, colorScheme: 'gray', route: '/dashboard/settings' },
    { id: 'profile', label: 'Profile', icon: FaUser, colorScheme: 'gray', route: '/dashboard/profile' },
  ];

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item =>
    canAccessTab(authenticatedUser || null, item.id, organization)
  );

  const location = useLocation();
  return (
    <Box
      w={isMinimized ? '80px' : '280px'}
      bg={sidebarBg}
      boxShadow="xl"
      borderRight="1px"
      borderColor={borderColor}
      position="sticky"
      top={0}
      h="100vh"
      transition="width 0.3s ease"
      zIndex={10}
    >
      <VStack align="stretch" spacing={0} h="full">
        {/* Header */}
        <Box p={isMinimized ? 4 : 8} borderBottom="1px" borderColor={borderColor}>
          <Flex align="center" justify="space-between" mb={isMinimized ? 0 : 4}>
            {!isMinimized && (
              <Flex align="center">
                {/* <Icon as={FaTasks} boxSize={8} color="blue.500" mr={3} /> */}
                <Heading size="lg" color="blue.600" display={'flex'} alignItems={'center'}>
                  <img src={Logo} alt={`${APP_NAME} logo`} style={{ height: 50, marginRight: 2 }} />
                  <Text p={0} fontSize="2xl" fontWeight="bold">
                    {APP_NAME}
                  </Text>
                </Heading>
              </Flex>
            )}
            {isMinimized && (
              <Tooltip label={APP_NAME} placement="right">
                  <img src={Logo} alt={`${APP_NAME} logo`} style={{ height: 50, marginRight: 2 }} />
              </Tooltip>
            )}
          </Flex>

          {/* Toggle Button */}
          <Flex justify={isMinimized ? 'center' : 'flex-end'} mb={isMinimized ? 4 : 0}>
            <Tooltip label={isMinimized ? 'Expand Sidebar' : 'Minimize Sidebar'} placement="right">
              <IconButton
                aria-label={isMinimized ? 'Expand Sidebar' : 'Minimize Sidebar'}
                icon={<Icon as={isMinimized ? FaChevronRight : FaChevronLeft} />}
                size="sm"
                bg={isMinimized ? 'transparent' : 'orange.200'}
                variant={'solid'}
                onClick={onToggleMinimize}
                _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
                position={isMinimized ? 'absolute' : 'absolute'}
                right={isMinimized ? 0 : '0'}
                top={'24px'}
                borderTopRightRadius={0}
                borderBottomRightRadius={0}
              />
            </Tooltip>
          </Flex>

          {/* User Info */}
          {!isMinimized && (
            <Flex align="center">
              <Avatar
                size="md"
                name={`${userInfo?.firstName} ${userInfo?.lastName}`}
                bg="blue.500"
                mr={3}
              />
              <Box>
                <Text fontWeight="bold" fontSize="sm">
                  {userInfo?.firstName} {userInfo?.lastName}
                </Text>
                <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                  {userInfo?.role?.toUpperCase()}
                </Badge>
              </Box>
            </Flex>
          )}

          {isMinimized && (
            <Flex justify="center">
              <Tooltip
                label={`${userInfo?.firstName} ${userInfo?.lastName} (${userInfo?.role?.toUpperCase()})`}
                placement="right"
              >
                <Avatar
                  size="md"
                  name={`${userInfo?.firstName} ${userInfo?.lastName}`}
                  bg="blue.500"
                />
              </Tooltip>
            </Flex>
          )}
        </Box>

        {/* Navigation */}
        <VStack align="stretch" spacing={2} p={isMinimized ? 2 : 6} flex={1}>
          {menuItems.map(item => {
            const isActive = location.pathname === item.route;
            return (
              <Tooltip key={item.id} label={item.label} placement="right" isDisabled={!isMinimized}>
                <Button
                  as={Link}
                  to={item.route}
                  leftIcon={<Icon as={item.icon} />}
                  variant={isActive ? 'solid' : 'ghost'}
                  colorScheme={isActive ? item.colorScheme : 'gray'}
                  justifyContent={isMinimized ? 'center' : 'flex-start'}
                  size={isMinimized ? 'md' : 'lg'}
                  fontWeight="800"
                  _hover={
                    !isActive
                      ? { bg: `${item.colorScheme}.50`, color: `${item.colorScheme}.600` }
                      : {}
                  }
                  borderRadius="xl"
                  px={isMinimized ? 2 : 4}
                  minH={isMinimized ? '48px' : 'auto'}
                >
                  {!isMinimized && item.label}
                </Button>
              </Tooltip>
            );
          })}

          <Divider my={4} />

          <Tooltip label="Logout" placement="right" isDisabled={!isMinimized}>
            <Button
              leftIcon={<Icon as={FaSignOutAlt} />}
              colorScheme="red"
              variant="ghost"
              justifyContent={isMinimized ? 'center' : 'flex-start'}
              size={isMinimized ? 'md' : 'lg'}
              fontWeight="medium"
              onClick={onLogout}
              borderRadius="xl"
              _hover={{ bg: 'red.50' }}
              px={isMinimized ? 2 : 4}
              minH={isMinimized ? '48px' : 'auto'}
            >
              {!isMinimized && 'Logout'}
            </Button>
          </Tooltip>
        </VStack>
      </VStack>
    </Box>
  );
}
export default Sidebar;
